import classNames from 'classnames'
import { forwardRef, HTMLAttributes } from 'react'

import { ensurePropOf, Slot } from '../utilities'
import { assignSubComponents } from '../utilities/internal/assign-sub-components'
import styles from './chat-message.module.scss'
import { ChatMessageBody } from './chat-message-body'
import { ChatMessageContent } from './chat-message-content'
import { ChatMessageFooterControls } from './chat-message-footer-controls'
import { ChatMessageHeader } from './chat-message-header'
import { ChatMessageHeaderLabel } from './chat-message-header-label'
import { ChatMessageContext, ChatMessageContextType } from './context'
import { ChatMessageFrom, ChatMessageSize, ChatMessageSizes } from './types'

export type ChatMessageElement = HTMLDivElement

export interface ChatMessageProps extends HTMLAttributes<ChatMessageElement> {
  size?: ChatMessageSize
  from?: ChatMessageFrom
  loading?: boolean
  asChild?: boolean
}

const ChatMessageRoot = forwardRef<ChatMessageElement, ChatMessageProps>(function ChatMessage(
  props,
  forwardedRef
) {
  const {
    className,
    size: sizeProp,
    from: fromProp,
    asChild,
    children,
    loading = false,
    ...rest
  } = props

  const from = ensurePropOf<ChatMessageFrom>(ChatMessageFrom, fromProp, 'other')
  const size = ensurePropOf<ChatMessageSize>(ChatMessageSizes, sizeProp, 'md')

  const context: ChatMessageContextType = {
    from,
    size,
    loading,
  }

  const classes = classNames(
    styles.root,
    {
      [styles.me]: from === 'me',
    },
    className
  )

  const Component = asChild ? Slot : 'div'

  return (
    <ChatMessageContext.Provider value={context}>
      <Component {...rest} className={classes} ref={forwardedRef}>
        {children}
      </Component>
    </ChatMessageContext.Provider>
  )
})

export const ChatMessage = assignSubComponents('ChatMessage', ChatMessageRoot, {
  Header: ChatMessageHeader,
  HeaderLabel: ChatMessageHeaderLabel,
  Content: ChatMessageContent,
  Body: ChatMessageBody,
  FooterControls: ChatMessageFooterControls,
})

export type * from './chat-message-body'
export type * from './chat-message-content'
export type * from './chat-message-footer-controls'
export type * from './chat-message-header'
export type * from './chat-message-header-label'
export type * from './types'
